import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DIGESTS } from "@/constants/testIds";
import { toast } from "sonner";
import { Sparkles, Calendar, ChevronRight } from "lucide-react";
import ExportButton from "@/components/ExportButton";
import SearchBar, { useLocalFilter } from "@/components/SearchBar";

export default function Digests() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const filtered = useLocalFilter(items, q, ["summary", "kind"]);
  const load = async () => setItems((await api.get("/digests")).data);
  useEffect(() => { load(); }, []);

  const generate = async (kind) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/digests/generate?kind=${kind}`);
      toast.success(`${kind} digest generated`);
      setSelected(data);
      load();
    } catch {
      toast.error("Failed. Ensure Emergent LLM key is set and has balance.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={DIGESTS.root} className="p-8 lg:p-12 max-w-[1300px]">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">AI Supervisor</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Digests</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            AI-generated summaries of conversion activity — sent to your admin WhatsApp automatically at 01:00 UTC (daily) and Monday 01:30 UTC (weekly).
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButton path="/exports/digests.xlsx" filename="digests.xlsx" testId="digests-export-btn" />
          <button
            data-testid={DIGESTS.generateDailyBtn}
            onClick={() => generate("daily")}
            disabled={busy}
            className="inline-flex items-center gap-2 bg-orange-600 text-white text-xs font-semibold px-3 py-2 rounded-md hover:bg-orange-500 transition-colors duration-150 disabled:opacity-60"
          >
            <Sparkles className={`h-3.5 w-3.5 ${busy ? "animate-pulse" : ""}`} /> Generate Daily
          </button>
          <button
            data-testid={DIGESTS.generateWeeklyBtn}
            onClick={() => generate("weekly")}
            disabled={busy}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white text-xs font-semibold px-3 py-2 rounded-md hover:bg-indigo-500 transition-colors duration-150 disabled:opacity-60"
          >
            <Calendar className={`h-3.5 w-3.5 ${busy ? "animate-pulse" : ""}`} /> Generate Weekly
          </button>
        </div>
      </div>

      <div className="mb-4"><SearchBar value={q} onChange={setQ} placeholder="Search digests…" testId="digests-search" /></div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-2">
          {filtered.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-10 border border-dashed border-slate-200 rounded-lg">
              No digests match.
            </div>
          )}
          {filtered.map((d) => (
            <button
              key={d.id}
              data-testid={`${DIGESTS.itemPrefix}${d.id}`}
              onClick={() => setSelected(d)}
              className={`w-full text-left border rounded-lg p-4 transition-colors duration-150 ${
                selected?.id === d.id ? "border-orange-500 bg-orange-50" : "border-slate-200 bg-white hover:bg-slate-50"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200">
                    {d.kind}
                  </span>
                  <div className="font-heading font-bold text-sm mt-2">
                    {new Date(d.created_at).toLocaleDateString()}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {d.metrics.new_users} new · {d.metrics.converted} converted · {d.metrics.conversion_rate}% CR
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-400" />
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6 min-h-[400px]">
          {!selected && (
            <div className="text-sm text-slate-500 text-center py-20">
              Select a digest from the list, or generate a new one.
            </div>
          )}
          {selected && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200">
                  {selected.kind}
                </span>
                <div className="text-xs text-slate-500">
                  {new Date(selected.period_start).toLocaleDateString()} → {new Date(selected.period_end).toLocaleDateString()}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  ["New Users", selected.metrics.new_users],
                  ["Converted", selected.metrics.converted],
                  ["CR %", selected.metrics.conversion_rate],
                  ["AI Msgs", selected.metrics.ai_messages],
                ].map(([l, v]) => (
                  <div key={l} className="border border-slate-200 rounded-md p-3">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{l}</div>
                    <div className="font-heading font-extrabold text-xl tabular mt-1">{v ?? "—"}</div>
                  </div>
                ))}
              </div>
              <article className="prose prose-sm max-w-none text-slate-800 whitespace-pre-wrap font-body">
                {selected.summary}
              </article>
              {selected.sent_to?.length > 0 && (
                <div className="mt-6 text-xs text-slate-500 border-t border-slate-200 pt-3">
                  Sent to: {selected.sent_to.join(", ")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
